import { generatePath } from 'react-router-dom';
import { ApiBaseService } from '../api-base-service';
import type {
    IAnonymousAppointment,
    IAppointment,
    IAppointmentBranch,
    IAppointmentSettings,
    IContactRequest,
    IDownloadIcalRequest,
    IFetchAdvisorsRequest,
    IFetchRmTimeslotsRequest,
    IFetchTimeslotsRequest,
    IRule,
    ITimeslot,
    ITopic,
} from '../../domain';
import i18n from '../../../../i18n';

export enum Endpoints {
    APPOINTMENTS = '/appointments',
    BRANCHES = '/branches',
    RELATIONSHIP_MANAGER = '/relationship-manager',
    RULES = '/rules',
    SETTINGS = '/settings',
    TOPICS = '/topics',
}

export const appointmentsApiUrls = {
    bookAnonymousAppointment: `${Endpoints.APPOINTMENTS}/anonymous`,
    bookAppointment: Endpoints.APPOINTMENTS,
    downloadIcal: `${Endpoints.APPOINTMENTS}/:appointmentId/ical`,
    fetchAdvisorsByBranchAndTopic: `${Endpoints.BRANCHES}/:branchId/topics/:topicId/advisors`,
    fetchBranches: Endpoints.BRANCHES,
    fetchRules: Endpoints.RULES,
    fetchSettings: Endpoints.SETTINGS,
    fetchTimeslotsByRelationshipManager: `${Endpoints.RELATIONSHIP_MANAGER}/:rmId/topics/:topicId/appointment-types/:appointmentType/time-slots`,
    fetchTimeslotsByBranchAndTopic: `${Endpoints.TOPICS}/:topicId/branches/:branchId/time-slots`,
    fetchTopics: Endpoints.TOPICS,
    sendContactRequest: `${Endpoints.BRANCHES}/:branchId/topics/:topicId/contact-request`,
};

export class AppointmentsApiService extends ApiBaseService {
    constructor(context = '/delegate/appointments/v4') {
        super(context);
    }

    async bookAnonymousAppointment(appointment: IAnonymousAppointment): Promise<IAnonymousAppointment> {
        const { data } = await this.http.post(appointmentsApiUrls.bookAnonymousAppointment, appointment);

        return data;
    }

    async bookAppointment(appointment: IAppointment): Promise<IAppointment> {
        const { data } = await this.http.post(appointmentsApiUrls.bookAppointment, appointment);

        return data;
    }

    async downloadIcal({ appointmentId, anonymousCustomerId }: IDownloadIcalRequest): Promise<Blob> {
        const { data } = await this.http.get<BlobPart>(generatePath(appointmentsApiUrls.downloadIcal, { appointmentId }), {
            params: { lang: i18n.language, anonymousCustomerId },
        });

        return new Blob([data], { type: 'text/calendar' });
    }

    async fetchBranches(): Promise<IAppointmentBranch[]> {
        const { data } = await this.http.get<IAppointmentBranch[]>(appointmentsApiUrls.fetchBranches);

        return data;
    }

    async fetchRelationshipManagerTimeslots({ appointmentType, rmId, topicId }: IFetchRmTimeslotsRequest): Promise<ITimeslot[]> {
        const { data } = await this.http.get<ITimeslot[]>(
            generatePath(appointmentsApiUrls.fetchTimeslotsByRelationshipManager, { appointmentType, rmId, topicId }),
        );

        return data;
    }

    async fetchAdvisorsByBranchAndTopic({ branchId, topicId }: IFetchAdvisorsRequest): Promise<string[]> {
        const { data } = await this.http.get<string[]>(generatePath(appointmentsApiUrls.fetchAdvisorsByBranchAndTopic, { branchId, topicId }));

        return data;
    }

    async fetchRules(): Promise<IRule[]> {
        const { data } = await this.http.get<IRule[]>(appointmentsApiUrls.fetchRules);

        return data;
    }

    async fetchSettings(): Promise<IAppointmentSettings> {
        const { data } = await this.http.get<IAppointmentSettings>(appointmentsApiUrls.fetchSettings);

        return data;
    }

    async fetchTimeslots({ topicId, branchId }: IFetchTimeslotsRequest): Promise<ITimeslot[]> {
        const { data } = await this.http.get<ITimeslot[]>(generatePath(appointmentsApiUrls.fetchTimeslotsByBranchAndTopic, { branchId, topicId }));

        return data;
    }

    async fetchTopics(): Promise<ITopic[]> {
        const { data } = await this.http.get<ITopic[]>(appointmentsApiUrls.fetchTopics);

        return data;
    }

    async sendContactRequest(request: IContactRequest): Promise<any> {
        const branchId = request.branch.id;
        const topicId = request.topic.id;
        const { data } = await this.http.post<any>(generatePath(appointmentsApiUrls.sendContactRequest, { branchId, topicId }), request);

        return data;
    }
}
